import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { InternalPage } from "../../../components/primitives/layout/InternalPage";
import { LoadingState } from "../../../components/primitives/feedback/LoadingState";
import { EmptyState } from "../../../components/primitives/feedback/EmptyState";
import { ConfirmDialog } from "../../../components/primitives/feedback/ConfirmDialog";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../components/primitives/data-display/DataSequence";
import { usePopupMenu } from "../../../components/primitives/navigation/PopupMenu";
import { InsightStatusPills } from "../../../components/domain/data-display/InsightCard/InsightStatusPills";
import { useConfirmHandler } from "../../../hooks/useConfirmHandler";
import { useLocaleFormatters } from "../../../hooks/useLocaleFormatters";
import { RowTitleLink } from "../../../components/primitives/data-display/RowTitleLink";
import { SectionTitle } from "../../../components/primitives/data-display/SectionTitle";
import { Note } from "../../../components/primitives/data-display/Note";
import { Caption } from "../../../components/primitives/data-display/Caption";
import type { ExpertInsightSummary } from "../../../lib/expert_insights_api";
import { ellipsis } from "../../../lib/ellipsis";
import { useTaxonomy } from "../../../context/TaxonomyProvider";
import type { TaxonomyNodeView } from "../../../lib/taxonomyView";
import { useInsightList } from "./useInsightList";

interface TitleCellProps {
  insight: ExpertInsightSummary;
  onEdit: () => void;
}

function TitleCell({ insight, onEdit }: TitleCellProps) {
  const { t } = useTranslation();
  const title =
    insight.draft?.title ?? insight.currentVersion?.title ?? t("expertInsights.insights.untitled");
  const body = insight.draft?.body ?? insight.currentVersion?.body ?? "";

  return (
    <Stack sx={{ minWidth: 0, gap: 0.25 }}>
      <RowTitleLink onClick={onEdit}>{title}</RowTitleLink>
      {body && <Caption>{ellipsis(body, 120)}</Caption>}
    </Stack>
  );
}

interface InsightRowActionsProps {
  insight: ExpertInsightSummary;
  onEdit: () => void;
  onWithdraw: () => void;
  onDeleteDraft: () => void;
}

function InsightRowActions({ insight, onEdit, onWithdraw, onDeleteDraft }: InsightRowActionsProps) {
  const { t } = useTranslation();
  const canWithdraw = insight.status === "active" && insight.currentVersion !== null;
  const canDeleteDraft = insight.draft !== null && insight.currentVersion === null;

  const { menu, setPopupMenuAnchor } = usePopupMenu([
    [{ label: t("expertInsights.insights.edit"), onClick: onEdit }],
    ...(canDeleteDraft
      ? [
          [
            {
              label: t("expertInsights.insights.deleteDraft"),
              onClick: onDeleteDraft,
              color: "error" as const,
            },
          ],
        ]
      : []),
    ...(canWithdraw
      ? [[{ label: t("expertInsights.insights.withdraw"), onClick: onWithdraw }]]
      : []),
  ]);

  if (insight.status === "withdrawn") return null;

  return (
    <>
      <IconButton
        size="small"
        onClick={setPopupMenuAnchor}
        aria-label={t("expertInsights.insights.actions")}
      >
        <MoreVertIcon />
      </IconButton>
      {menu}
    </>
  );
}

/** Lists an expert's insights in a searchable, field-filtered, paginated table. */
export default function InsightList() {
  const { t } = useTranslation();
  const { formatDate } = useLocaleFormatters();
  const {
    paginatedInsights,
    totalPages,
    page,
    query,
    fieldFilter,
    authorizedFields,
    noAuthorizedFields,
    loading,
    error,
    setQuery,
    setFieldFilter,
    setPage,
    handleNew,
    handleEdit,
    handleWithdraw,
    handleDeleteDraft,
  } = useInsightList();

  const fieldOptions = useTaxonomy().fields.filter((n) => authorizedFields.includes(n.urn));

  const withdraw = useConfirmHandler({
    title: t("expertInsights.insights.withdrawConfirmTitle"),
    message: t("expertInsights.insights.withdrawConfirmMessage"),
    confirmLabel: t("expertInsights.insights.withdraw"),
  });

  const deleteDraft = useConfirmHandler({
    title: t("expertInsights.insights.deleteDraftConfirmTitle"),
    message: t("expertInsights.insights.deleteDraftConfirmMessage"),
    confirmLabel: t("expertInsights.insights.deleteDraft"),
    confirmColor: "error",
  });

  const columns: DataSequenceColumn<ExpertInsightSummary>[] = [
    {
      key: "title",
      header: t("expertInsights.list.columns.title"),
      cardRole: "primary",
      cell: (row) => <TitleCell insight={row} onEdit={() => handleEdit(row.id)} />,
    },
    {
      key: "status",
      header: t("expertInsights.list.columns.status"),
      cell: (row) => <InsightStatusPills insight={row} />,
    },
    {
      key: "lastEdit",
      header: t("expertInsights.list.columns.lastEdit"),
      cell: (row) => formatDate(row.currentVersion?.publishedAt ?? row.createdAt),
    },
    {
      key: "actions",
      header: "",
      cardRole: "action",
      align: "right",
      cell: (row) => (
        <InsightRowActions
          insight={row}
          onEdit={() => handleEdit(row.id)}
          onWithdraw={() => withdraw.handle(() => handleWithdraw(row.id))}
          onDeleteDraft={() => deleteDraft.handle(() => handleDeleteDraft(row.id))}
        />
      ),
    },
  ];

  return (
    <InternalPage width="full">
      <Stack gap={2}>
        {noAuthorizedFields && (
          <Alert severity="warning">{t("expertInsights.list.noAuthorizedFieldsBanner")}</Alert>
        )}
        <Stack gap={0.5}>
          <SectionTitle>{t("expertInsights.insights.title")}</SectionTitle>
          <Note>{t("expertInsights.list.description")}</Note>
        </Stack>

        <Alert severity="info">{t("expertInsights.list.guidanceNote")}</Alert>

        <Stack
          direction="row"
          gap={1}
          flexWrap="wrap"
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Stack direction="row" gap={1} flexWrap="wrap" sx={{ alignItems: "center" }}>
            <TextField
              size="small"
              placeholder={t("expertInsights.list.searchPlaceholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{ minWidth: 320, maxWidth: 400 }}
            />
            {fieldOptions.length > 0 && (
              <Autocomplete<TaxonomyNodeView>
                options={fieldOptions}
                getOptionLabel={(n) => n.enName}
                value={fieldOptions.find((n) => n.urn === fieldFilter) ?? null}
                onChange={(_, n) => setFieldFilter(n?.urn ?? null)}
                size="small"
                sx={{ minWidth: 300 }}
                renderInput={(params) => (
                  <TextField {...params} label={t("expertInsights.list.fieldFilterLabel")} />
                )}
              />
            )}
          </Stack>
          <Button variant="contained" onClick={handleNew}>
            {t("expertInsights.insights.newInsight")}
          </Button>
        </Stack>

        {loading && <LoadingState />}
        {error && <EmptyState message={error} />}

        {!loading && !error && (
          <DataSequence
            columns={columns}
            rows={paginatedInsights}
            getRowId={(row) => row.id}
            emptyMessage={t("expertInsights.insights.empty.message")}
          />
        )}

        {!loading && !error && (
          <Stack direction="row" sx={{ justifyContent: "center" }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, p) => setPage(p)}
              size="small"
            />
          </Stack>
        )}
      </Stack>

      <ConfirmDialog {...withdraw.dialogProps} />
      <ConfirmDialog {...deleteDraft.dialogProps} />
    </InternalPage>
  );
}
